import React, { useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { Box as MuiBox } from '@material-ui/core'
import { InputField, Button } from './'

import {
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@material-ui/icons'
import useStyles from './styles'

export const FilterBar = React.memo((props) => {
  const { primaryFields, secondaryFields, filterData, onFilterDataChange, primaryButtons, searchRef } = props
  const classes = useStyles()

  const onFieldChange = useCallback((fieldName) => (value) => {
    onFilterDataChange(fieldName, value)
  }, [onFilterDataChange])

  const getFieldValue = useCallback((fieldName, field) => {
    if (field?.multipleOptions) {
      return filterData?.[fieldName]?.toString()?.split(',') || []
    }
    return filterData && filterData[fieldName]
  }, [filterData])

  const getDefaultFieldProps = useCallback((field, barVariant) => {
    let defaultFieldProps = {
      showLabel: false,
      // hideOptionArrow: true,
      clearButton: "auto",
      tintSelected: true
    }

    if (field.kind === "search") {
      defaultFieldProps = {
        ...defaultFieldProps,
        icon: SearchIcon,
        inputRef: searchRef
      }
    }
    if (field.kind === "button") {
      defaultFieldProps = {
        ...defaultFieldProps,
        className: classes.filterButton
      }
    }
    if (field.kind === "filter") {
      defaultFieldProps = {
        ...defaultFieldProps,
        icon: FilterIcon,
      }
    }
    if (field?.multipleOptions) {
      defaultFieldProps = {
        ...defaultFieldProps,
        tintSelected: getFieldValue(field.name, field)?.length > 0
      }
    }
    if (barVariant === "primary") {
      defaultFieldProps = {
        ...defaultFieldProps,
        variant: "filled",
        fullWidth: true,
      }
    } else if (barVariant === "secondary") {
      defaultFieldProps = {
        ...defaultFieldProps,
        size: "small",
      }
    }

    return defaultFieldProps
  }, [classes.filterButton, getFieldValue, searchRef])

  const createInputField = useCallback((field, barVariant, index) => {
    const defaultFieldProps = getDefaultFieldProps(field, barVariant)

    return (
      <div className={clsx(
        barVariant === "primary" && classes.filterBarPrimaryField,
        barVariant === "secondary" && classes.filterBarSecondaryField,
        barVariant === "primary" && field.kind === "search" && classes.filterBarSearchField,
        barVariant === "secondary" && field?.type !== "option" && classes.filterBarAddedHeight,
        barVariant === "secondary" && field?.type === "date-picker" && classes.filterBarWidthLimit
      )}
        key={`${field.name}-${index}-${barVariant}`}
      >
        {
          field.kind === "button" ? (
            <Button {...defaultFieldProps} {...field} />
          ) : (
            <InputField
              {...defaultFieldProps}
              {...field}
              isFilter
              onChange={onFieldChange(field.name)}
              value={getFieldValue(field.name, field)}
            />
          )}
      </div>
    )
  }, [getDefaultFieldProps, classes, onFieldChange, getFieldValue])

  const renderedPrimaryFields = useMemo(() => {
    if (!primaryFields) return null

    return primaryFields.map((field, i) => createInputField(field, "primary", i))
  }, [primaryFields, createInputField])

  const renderedSecondaryFields = useMemo(() => {
    if (!secondaryFields) return null

    return secondaryFields.map((field, i) => createInputField(field, "secondary", i))
  }, [secondaryFields, createInputField])

  const renderedPrimaryButtons = useMemo(() => {
    if (!primaryButtons) return null

    return primaryButtons.map((b, i) =>
      <Button color="primary" {...b} key={i} />
    ).reduce((p, n) => p ?
      [...p, <MuiBox ml={1} />, n] : [n]
      , null)
  }, [primaryButtons])

  return (
    <div className={classes.filterBar}>
      {primaryFields && (
        <div className={classes.filterBarPrimaryRow}>
          {renderedPrimaryFields}
          {renderedPrimaryButtons}
        </div>
      )}
      {secondaryFields && (
        <div className={classes.filterBarSecondaryRow}>
          {renderedSecondaryFields}
        </div>
      )}
    </div>
  )
}, (prevProps, nextProps) => {
  return (
    prevProps.primaryFields === nextProps.primaryFields &&
    prevProps.secondaryFields === nextProps.secondaryFields &&
    prevProps.primaryButtons === nextProps.primaryButtons &&
    prevProps.filterData === nextProps.filterData &&
    prevProps.onFilterDataChange === nextProps.onFilterDataChange
  )
})

export default FilterBar
